import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

type Phase = "starting" | "redirecting" | "error";

/**
 * Standalone Cashfree subscription checkout page for mobile webviews.
 *
 * The mobile app (iOS/Android):
 *   1. calls POST /subscriptions/create → gets subscription_session_id + mode
 *   2. opens this page in a webview / in-app browser:
 *        /pay/cashfree?subsSessionId=<id>&mode=<sandbox|production>
 *
 * This page runs the EXACT same Cashfree JS SDK flow the web SubscriptionPage
 * uses — the SDK builds the payment URL internally and redirects. After the
 * mandate, Cashfree redirects to the backend-configured return_url
 * (/subscription/success). The app detects that redirect and calls
 * GET /subscriptions/me/status to activate the tier.
 */
export default function CashfreePayPage() {
  const [params] = useSearchParams();
  const subsSessionId = params.get("subsSessionId") || "";
  const mode = params.get("mode") === "production" ? "production" : "sandbox";

  const [phase, setPhase] = useState<Phase>("starting");
  const [message, setMessage] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    async function launch() {
      if (!subsSessionId) {
        setPhase("error");
        setMessage("Missing subscription session. Please retry from the app.");
        return;
      }

      const w = window as unknown as {
        Cashfree?: (opts: { mode: string }) => {
          subscriptionsCheckout: (opts: {
            subsSessionId: string;
            redirectTarget?: string;
          }) => Promise<unknown>;
        };
      };

      try {
        if (!w.Cashfree) {
          await new Promise<void>((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
            s.onload = () => resolve();
            s.onerror = () => reject(new Error("Failed to load Cashfree SDK"));
            document.body.appendChild(s);
          });
        }
        if (!w.Cashfree) {
          throw new Error("Cashfree checkout unavailable");
        }

        const cashfree = w.Cashfree({ mode });
        setPhase("redirecting");
        // Same-tab redirect. Cashfree navigates to its payment page, then
        // redirects to the backend-configured return_url after the mandate.
        await cashfree.subscriptionsCheckout({
          subsSessionId,
          redirectTarget: "_self",
        });
      } catch (err) {
        setPhase("error");
        setMessage(
          err instanceof Error ? err.message : "Could not start checkout."
        );
      }
    }

    launch();
  }, [subsSessionId, mode]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm max-w-sm w-full p-8 text-center">
        {phase !== "error" ? (
          <>
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm font-semibold text-gray-800 mb-1">
              {phase === "redirecting"
                ? "Opening secure payment…"
                : "Preparing checkout…"}
            </p>
            <p className="text-sm text-gray-500">
              Please wait, do not close this window.
            </p>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4 text-2xl">
              !
            </div>
            <p className="text-sm font-semibold text-gray-800 mb-2">
              Checkout could not start
            </p>
            <p className="text-sm text-gray-500">{message}</p>
          </>
        )}
      </div>
    </div>
  );
}
